import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import StyledList from './StyledList';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers';
import BasicDateCalendar from './DatePickerComp';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { NumericFormat } from 'react-number-format';

const ExpensesForm = ({ state, updateField, errors, beneficiaries, productions, production_category_item, }) => {
    const [assignedEpisodes, setAssignedEpisodes] = useState([]);
    const [totalEpisodes, setTotalEpisodes] = useState([]);
    const [selectedProduction, setSelectedProduction] = useState("");
    const [file, setFile] = useState(null);
    const [isPrediction, setIsPrediction] = useState(state?.exp_pred || false);
    const [selectedDate, setSelectedDate] = useState(state?.exp_date || null);
    const [dragActive, setDragActive] = useState(false);

    useEffect(() => {
        if (selectedProduction && productions) {
            const selectedProd = productions.find(prod => prod.id === selectedProduction);
            if (selectedProd) {
                const allEpisodes = Array.from({ length: selectedProd.no_of_episodes }, (_, i) => (i + 1));
                // Filter out locked episodes
                const lockedEpisodes = Array.isArray(selectedProd.locked_episodes) ? selectedProd.locked_episodes : [];
                const availableEpisodes = allEpisodes.filter(ep => !lockedEpisodes.includes(ep));
                setTotalEpisodes(availableEpisodes);
            }
        }
    }, [selectedProduction, productions]);

    useEffect(() => {
        if (state?.p_id) {
            setSelectedProduction(state.p_id);
        }
    }, [state?.p_id]);

    useEffect(() => {
        setIsPrediction(state?.exp_pred || false);
    }, [state?.exp_pred]);

    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
        updateField("exp_date", newDate ? newDate : null);
    };

    const handleProductionChange = (e) => {
        const selectedProductionId = e.target.value;
        setSelectedProduction(selectedProductionId);

        const selectedProd = productions.find(prod => prod.id === selectedProductionId);
        if (selectedProd) {
            const allEpisodes = Array.from({ length: selectedProd.no_of_episodes }, (_, i) => (i + 1));
            // Filter out locked episodes
            const lockedEpisodes = Array.isArray(selectedProd.locked_episodes) ? selectedProd.locked_episodes : [];
            const availableEpisodes = allEpisodes.filter(ep => !lockedEpisodes.includes(ep));
            setAssignedEpisodes(availableEpisodes);
            // Clear previously selected episodes that might be locked
            const currentSelected = state?.exp_assigned_episodes || [];
            const validSelected = currentSelected.filter(ep => !lockedEpisodes.includes(ep));
            if (validSelected.length !== currentSelected.length) {
                updateField("exp_assigned_episodes", validSelected);
            }
        }
        updateField("p_id", selectedProductionId);
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            updateField("file", selectedFile);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            updateField("file", droppedFile);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        updateField("file", null);
    };

    const handlePredictionChange = (e) => {
        const checked = e.target.checked;
        setIsPrediction(checked);
        updateField("exp_pred", checked);
    };

    return (
        <Box p={2} display="flex" justifyContent="center" >
            <Grid container spacing={2} >

                {/* Left Side Form Fields */}
                <Grid item xs={12} sm={6} >
                    <Grid container direction="column" spacing={2} paddingTop={1}>

                        {/* Is Prediction Checkbox */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <Box
                                    sx={{
                                        marginBottom: '15px',
                                        border: '1px solid #666666a6',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        backgroundColor: isPrediction ? '#e0ffe0' : 'transparent',
                                        '&:hover': {
                                            borderColor: '#333333',
                                        },
                                        transition: 'border-color 0.3s, background-color 0.3s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '6px 12px',
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isPrediction}
                                                onChange={handlePredictionChange}
                                                color="primary"
                                                sx={{
                                                    padding: 0,
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: 22,
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: isPrediction ? 'green' : 'gray',
                                                    marginLeft: 1,
                                                }}
                                            >
                                                Πρόβλεψη
                                            </Typography>
                                        }
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            margin: 0,
                                        }}
                                    />
                                </Box>
                            </FormControl>
                        </Grid>

                        {/* Production ID */}
                        <Grid item maxWidth={'100%'}>
                            <FormControl fullWidth size="small" error={!!errors.p_id}>
                                <InputLabel id="prod-label">Παραγωγή</InputLabel>
                                <Select
                                    sx={{
                                        marginBottom: '15px',
                                    }}
                                    labelId="prod-label"
                                    id="prod-id"
                                    label="Production"
                                    value={selectedProduction}
                                    onChange={handleProductionChange}
                                    error={!!errors.p_id}
                                    helperText={errors.p_id || null}
                                    MenuProps={{
                                        PaperProps: {
                                            elevation: 2,
                                        },
                                    }}
                                >
                                    {productions?.map(prod => (
                                        <MenuItem key={prod.id} value={prod.id}>
                                            {prod.p_descr}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.p_id && <FormHelperText>{errors.p_id}</FormHelperText>}
                            </FormControl>
                        </Grid>

                        {/* PCI Item*/}
                        <Grid item>
                            <FormControl fullWidth size="small" error={!!errors.pci_id}>
                                <Autocomplete
                                    id="pci-id"
                                    options={production_category_item || []}
                                    getOptionLabel={(option) => option.pci_descr || ""}
                                    value={
                                        production_category_item ?
                                            production_category_item.find(item => item.id === state?.pci_id) || null :
                                            null
                                    }
                                    onChange={(event, newValue) => {
                                        const selectedId = newValue ? newValue.id : "";
                                        updateField("pci_id", selectedId);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Στοιχείο"
                                            error={!!errors.pci_id}
                                            helperText={errors.pci_id || null}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: 40,
                                                },
                                                '& .MuiInputLabel-root': {
                                                    fontSize: '0.975rem',
                                                    top: '-6px',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    top: 0,
                                                },
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    disableClearable
                                />
                            </FormControl>
                        </Grid>

                        {/* Beneficiary */}
                        <Grid item>
                            <FormControl fullWidth size="small" error={!!errors.exp_beneficiary_id}>
                                <Autocomplete
                                    id="exp-beneficiary-id"
                                    options={beneficiaries || []}
                                    getOptionLabel={(option) => option.eb_company_name || ""}
                                    value={
                                        beneficiaries ?
                                            beneficiaries.find(b => b.id === state?.exp_beneficiary_id) || null :
                                            null
                                    }
                                    onChange={(event, newValue) => {
                                        const selectedId = newValue ? newValue.id : "";
                                        updateField("exp_beneficiary_id", selectedId);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Συναλλασσόμενος"
                                            error={!!errors.exp_beneficiary_id}
                                            helperText={errors.exp_beneficiary_id || null}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: 40,
                                                },
                                                '& .MuiInputLabel-root': {
                                                    fontSize: '0.975rem',
                                                    top: '-6px',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    top: 0,
                                                },
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    disableClearable
                                />
                            </FormControl>
                        </Grid>

                        {/* Export Invoice Number */}
                        {!isPrediction && (
                            <Grid item>
                                <TextField
                                    required
                                    id="exp-inv-no-id"
                                    label="Αριθμός Τιμολογίου"
                                    size="small"
                                    fullWidth
                                    value={state?.exp_inv_no || ""}
                                    onChange={e => updateField("exp_inv_no", e.target.value)}
                                    helperText={errors.exp_inv_no || null}
                                    error={!!errors.exp_inv_no}
                                />
                            </Grid>
                        )}

                        {/* Invoice Amount */}
                        <Grid item>
                            <NumericFormat
                                required
                                size="small"
                                fullWidth
                                id="exp-amount-id"
                                label="Ποσό Τιμολογίου"
                                value={state?.exp_amount || ""}
                                onChange={(e) => {
                                    // Remove the '€' symbol and parse the float value
                                    const value = parseFloat(e.target.value.replace('€', '').replace(',', ''));
                                    updateField("exp_amount", isNaN(value) ? "" : value);
                                }}
                                customInput={TextField}
                                thousandSeparator
                                valueIsNumericString
                                prefix="€"
                                variant="outlined"
                                helperText={errors.exp_amount || null}
                                error={!!errors.exp_amount}
                                inputProps={{ step: "0.01", min: "0" }}
                            />
                        </Grid>

                        {/* Invoice Date */}
                        {!isPrediction && (
                            <Grid item>
                                <BasicDateCalendar
                                    id="exp-date"
                                    size="small"
                                    fullWidth
                                    format="DD-MM-YYYY"
                                    onDateChange={handleDateChange}
                                    value={state?.exp_date || null}
                                    updateField={updateField}
                                    selectedDates={state?.exp_date || null}
                                    helperText={errors.exp_date || ' '}
                                    error={!!errors.exp_date}
                                />
                            </Grid>
                        )}

                        {/* File Upload (Conditional) */}
                        {!isPrediction && (
                            <Grid item>
                                <Box sx={{ position: 'relative', width: '100%' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            position: 'absolute',
                                            top: '-10px',
                                            left: '16px',
                                            backgroundColor: 'white',
                                            padding: '0 4px',
                                            fontSize: '0.85rem',
                                            color: '#666666',
                                            zIndex: 1,
                                        }}
                                    >
                                        Αρχείο
                                    </Typography>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        sx={{
                                            border: dragActive ? `2px dashed #333` : `1px dashed #666`,
                                            border: `1px solid #666666a6`,
                                            borderRadius: 1,
                                            padding: 2,
                                            position: 'relative',
                                            '&:hover': {
                                                borderColor: '#333333',
                                            },
                                            cursor: 'pointer',
                                            backgroundColor: dragActive ? '#f9f9f9' : 'white',
                                            '&:hover': {
                                                borderColor: '#333333',
                                            },
                                        }}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            type="file"
                                            accept="
        application/pdf, 
        image/*, 
        application/msword, 
        application/vnd.openxmlformats-officedocument.wordprocessingml.document, 
        application/vnd.ms-excel, 
        application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, 
        .pdf, 
        .doc, 
        .docx, 
        .xls, 
        .xlsx
    "
                                            id="upload-button"
                                            style={{ display: 'none' }}
                                            onChange={handleFileChange}
                                        />
                                        <Box
                                            component="label"
                                            htmlFor="upload-button"
                                            sx={{
                                                wordBreak: 'break-all',
                                                display: 'block',
                                                textAlign: 'center',
                                                padding: '10px',
                                                border: '1px dashed #666',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                color: '#666',
                                                fontWeight: 'bold',
                                                '&:hover': {
                                                    borderColor: '#333333',
                                                },
                                            }}
                                        >
                                            {file ? file.name : state?.exp_original_filename || 'Επιλέξτε Τιμολόγιο'}
                                            {file && (
                                                <IconButton
                                                    aria-label="Remove file"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent triggering the file input
                                                        e.preventDefault(); // Prevent any default behavior
                                                        handleRemoveFile();
                                                    }}
                                                    sx={{
                                                        color: '#d32f2f',
                                                        marginLeft: 2,
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </FormControl>
                                </Box>
                            </Grid>
                        )}

                        {/* Notes Field */}
                        <Grid item>
                            <TextField
                                id="exp-notes-id"
                                label="Παρατηρήσεις"
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={4}
                                value={state?.exp_notes || ""}
                                onChange={(e) => updateField("exp_notes", e.target.value)}
                                helperText={errors.exp_notes || ''}
                                error={!!errors.exp_notes}
                            />
                        </Grid>

                    </Grid>
                </Grid>

                {/* Right Side Styled List */}
                <Grid item xs={12} sm={6} sx={{ paddingLeft: 10 }}>
                    <StyledList
                        total_episodes={totalEpisodes}
                        episodes={assignedEpisodes}
                        updateField={updateField}
                        selectedEpisodes={state?.exp_assigned_episodes || []}
                    />
                </Grid>

            </Grid>
        </Box >
    );
};

export default ExpensesForm; 
